import { useState } from 'react';
import type { ReactNode } from 'react';
import { useLive, useRuntime } from './context';
import { useRemote } from './useRemote';
import { formatClock, formatDayLong, isoDayIn } from '../mirror/time';
import { useLocale, useT } from '../messages';
import type { T } from '../messages';
import { InquiryAnswered, InquiryList, StaffRequest, StaffRequestList } from '../wire';
import type { Inquiry, StaffRequest as StaffRequestType } from '../wire';
import type { BookSheetPrefill } from './BookSheet';

/**
 * **The owner's queue** (`docs/lovespa-gaps-plan.md` A5) — the panel the interim
 * promised and could not build.
 *
 * Her own words, twice: *"I need to gently bring the new people in"*, and
 * *"it'll come up on my calendar here and here… and I get a text."* This is the
 * "here". A stranger picked a treatment and a time on her website, proved her
 * telephone number with a code, and the hour is **held** until she answers.
 *
 * ## Why it is not the inquiry list with buttons
 *
 * The inquiry panel beside this one is a read of a retention-bounded outbox: a
 * name and a number somebody typed into a public form, `not verified` on every
 * line, with no row to write a decision into. Everything about this one is the
 * opposite, and each difference is load-bearing:
 *
 *  - **The number is proved.** The guest claimed it by OTP before the request
 *    existed, which is what makes attaching it to a `clients` row safe — and
 *    what makes *"is she new"* answerable at all, because a stranger is a client
 *    with no prior visit.
 *  - **The time is real and it is held.** The request occupies the room, so the
 *    calendar behind this panel already shows it and nobody else is being
 *    offered it. That is also why it expires: stock of time taken off the shelf
 *    by somebody who never came back is stock lost.
 *  - **There is somewhere to put the answer.** Confirm, decline, propose — three
 *    verbs on a row, each of which moves the appointment and texts the guest.
 *
 * ## Offline
 *
 * Every action goes through the same `client_intent_id` the till and the
 * calendar use, so a tap in a tunnel is queued and replayed rather than lost,
 * and a double tap is one answer. The *list* is a `useRemote` read rather than a
 * mirror store, for the `Reports` reason: the mirror holds what this device
 * works on offline, and a request whose answer sends a text is not something
 * anybody can usefully decide about with no network — the row would be stale by
 * the time it mattered, and the hold may have expired.
 */
export function AppointmentRequests(): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const locale = useLocale();
  const session = useLive(() => runtime.mirror.session(), null);
  const timezone = session?.tenant.timezone ?? 'UTC';
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const requests = useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: '/requests',
        query: { limit: 50 },
        schema: StaffRequestList,
        signal,
      }),
    [],
    {
      // `booking.` rather than `inquiry.`: a request is a booking event now, and
      // `booking.requested` is the one this panel exists for. Confirming emits
      // `booking.created`, which also refreshes it — correctly, since the row
      // leaves the list.
      cue: (type) => type.startsWith('booking.'),
      pollMs: 60_000,
    },
  );

  const rows = requests.data?.requests ?? [];
  // Silence, not an empty panel: a tenant whose site books takes no requests at
  // all, and a heading over nothing every day would be chrome.
  if (rows.length === 0) return null;

  async function act(
    request: StaffRequestType,
    verb: 'confirm' | 'decline' | 'propose',
    body: Record<string, unknown>,
  ): Promise<void> {
    setBusy(request.request_id);
    setFailure(null);
    try {
      await runtime.api.request({
        method: 'POST',
        path: `/requests/${encodeURIComponent(request.request_id)}/${verb}`,
        body: { ...body, client_intent_id: intentId() },
        schema: StaffRequest,
      });
      setOpen(null);
      requests.reload();
    } catch (e) {
      // The one failure that is not a bug and has to read as a sentence: the
      // exclusion constraint fired at confirm time, which means the hour went to
      // somebody else while this request sat in the queue. Proposing another
      // time is the move, and saying so is better than a red box.
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="panel requests" aria-labelledby="appointment-requests-title">
      <h2 id="appointment-requests-title">{t('requests.appointmentsTitle')}</h2>
      {failure === null ? null : <p className="panel__error">{failure}</p>}
      <ul className="day-list day-list--compact">
        {rows.map((request) => (
          <li className="day-row" key={request.request_id}>
            <div className="day-row__time">
              <strong>{formatClock(request.slot.start, timezone, locale)}</strong>
              <small>
                {formatDayLong(isoDayIn(timezone, request.slot.start), timezone, locale)}
              </small>
            </div>

            <div className="day-row__who">
              <strong>{request.client.display_name}</strong>
              {/* The three facts the decision is made on, in the order she
                  needs them: is this somebody I know, how do I reach her, and
                  what did she ask for. */}
              {request.client.is_new ? <small className="tag">{t('requests.new')}</small> : null}
              {request.client.phone === null ? null : (
                <small>
                  {request.client.phone} · {t('requests.verified')}
                </small>
              )}
              <small>{request.service_name}</small>
              {request.note === '' ? null : <small>{request.note}</small>}
              {request.expires_at === null ? null : (
                <small className="muted">
                  {t('requests.holdsUntil', {
                    when: `${formatDayLong(
                      isoDayIn(timezone, request.expires_at),
                      timezone,
                      locale,
                    )} ${formatClock(request.expires_at, timezone, locale)}`,
                  })}
                </small>
              )}
              {request.proposed_by === 'staff' ? (
                <small className="muted">{t('requests.proposed')}</small>
              ) : null}
            </div>

            <div className="day-row__tail">
              {open === request.request_id ? (
                <RequestAnswer
                  request={request}
                  busy={busy === request.request_id}
                  timezone={timezone}
                  t={t}
                  onCancel={() => setOpen(null)}
                  onDecline={(message, trust) =>
                    void act(request, 'decline', {
                      ...(message === '' ? {} : { message }),
                      ...(trust === null ? {} : { may_book_online: trust }),
                    })
                  }
                  onPropose={(startIso) => void act(request, 'propose', { start: startIso })}
                />
              ) : (
                <div className="row-actions">
                  <button
                    className="button button--primary"
                    type="button"
                    disabled={busy === request.request_id}
                    // The plain yes: this hour, and nothing about the next
                    // one. Granting standing permission is the second button,
                    // because it is a second decision.
                    onClick={() => void act(request, 'confirm', {})}
                  >
                    {t('requests.confirm')}
                  </button>
                  {request.client.may_book_online ? null : (
                    <button
                      className="link-button"
                      type="button"
                      disabled={busy === request.request_id}
                      onClick={() => void act(request, 'confirm', { may_book_online: true })}
                    >
                      {t('requests.andTrust')}
                    </button>
                  )}
                  <button
                    className="link-button"
                    type="button"
                    onClick={() => setOpen(request.request_id)}
                  >
                    {t('requests.decline')}
                  </button>
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Messages — the anonymous inquiry list, and its one verb              */
/* ------------------------------------------------------------------ */

/**
 * **Messages** — the request-to-book / contact-form block of the guest site,
 * read for staff (`docs/lovespa-discovery.md` §2.3, `docs/tenant-site-offering.md`).
 *
 * Why it exists at all is the outbox's second reader problem: an inquiry is an
 * `inquiry.received` event, and a tenant with no webhook and an iPad that slept
 * through Sunday has no other way to learn somebody asked about a proposal
 * picnic. It reads `GET /v1/inquiries` through `useRemote` rather than the
 * mirror, for the `Reports` reason (`mirror/reports.ts`): the mirror holds what
 * this device works on offline, and a message that arrived over the network and
 * is answered by ringing somebody is not that. The cue is `inquiry.`, so a new
 * one lands in about a second and an answer on another device moves the row
 * here the same way.
 *
 * ## "Answered" is the server's (X1)
 *
 * This panel used to say, in its own comment, that there was no "answered" and
 * could not honestly be one: a local flag would be a queue whose state lives on
 * one iPad. That was the right refusal and it has been lifted the only way it
 * could be — `POST /v1/inquiries/{id}/answered` writes an `inquiry.answered`
 * event beside the inquiry, the list reads the instant back as `answered_at`,
 * and every device sees the same row move. So: one verb, **Mark answered**;
 * the row moves under an *Answered* heading rather than vanishing, because a
 * message somebody replied to on Tuesday is still worth finding on Thursday;
 * and the count beside the heading is the open ones only, since that is the
 * number she has to get to zero.
 *
 * **"Book this" is still not an answer** and still the only other verb: it
 * opens the booking sheet prefilled with the treatment asked for and *not* the
 * person, because a name and number typed into a public form are not a
 * `clients` row (`verified: false` on every line).
 */
export function Inquiries({ onBook }: { onBook: (prefill: BookSheetPrefill) => void }): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const locale = useLocale();
  const session = useLive(() => runtime.mirror.session(), null);
  const timezone = session?.tenant.timezone ?? 'UTC';
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const inquiries = useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: '/inquiries',
        query: { days: 30, limit: 25 },
        schema: InquiryList,
        signal,
      }),
    [],
    {
      cue: (type) => type.startsWith('inquiry.'),
      // Slower than the order pass on purpose: a request is answered in a
      // batch at the end of the day (§6 — she answers in batches, never on
      // Sundays), not watched like a ticket on a pass.
      pollMs: 60_000,
    },
  );

  const rows = inquiries.data?.inquiries ?? [];
  // Silence, not an empty panel: a tenant whose site takes bookings gets no
  // requests at all, and a heading over nothing every day would be chrome.
  if (rows.length === 0) return null;

  const open = rows.filter((r) => r.answered_at === null);
  const answered = rows.filter((r) => r.answered_at !== null);

  async function markAnswered(inquiry: Inquiry): Promise<void> {
    setBusy(inquiry.inquiry_id);
    setFailure(null);
    try {
      await runtime.api.request({
        method: 'POST',
        path: `/inquiries/${encodeURIComponent(inquiry.inquiry_id)}/answered`,
        body: { client_intent_id: intentId() },
        schema: InquiryAnswered,
      });
      inquiries.reload();
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const line = (request: Inquiry, tail: ReactNode): ReactNode => (
    <li
      className={`day-row${request.answered_at === null ? '' : ' day-row--answered'}`}
      key={request.inquiry_id}
    >
      <div className="day-row__time">
        <strong>{formatClock(request.received_at, timezone, locale)}</strong>
        <small>{t('requests.unverified')}</small>
      </div>

      <div className="day-row__who">
        <strong>{nameOf(request, t)}</strong>
        {/* Everything the guest actually said, in the order somebody
            reading it needs: how to reach them, what they want, when
            suits them, and anything else. An empty string draws nothing
            — a contact-form note has no treatment and should not print a
            blank line where one would be. */}
        {request.contact === '' ? null : <small>{request.contact}</small>}
        {request.item_name === '' ? null : <small>{request.item_name}</small>}
        {preferenceOf(request) === '' ? null : <small>{preferenceOf(request)}</small>}
        {request.message === '' ? null : <small>{request.message}</small>}
        {request.answered_at === null ? null : (
          <small className="muted">
            {t('requests.answeredAt', {
              when: `${formatDayLong(
                isoDayIn(timezone, request.answered_at),
                timezone,
                locale,
              )} ${formatClock(request.answered_at, timezone, locale)}`,
            })}
          </small>
        )}
      </div>

      <div className="day-row__tail">
        <div className="row-actions">{tail}</div>
      </div>
    </li>
  );

  const bookThis = (request: Inquiry): ReactNode => (
    <button
      className="link-button"
      type="button"
      onClick={() =>
        onBook({
          source: 'staff',
          ...(request.item_id === '' ? {} : { serviceId: request.item_id }),
          ...(request.name.trim() === '' ? {} : { guestName: request.name.trim() }),
        })
      }
    >
      {t('requests.book')}
    </button>
  );

  return (
    <section className="panel requests" aria-labelledby="requests-title">
      <h2 id="requests-title">
        {t('requests.inquiriesTitle')}
        {open.length === 0 ? null : (
          <span className="badge" aria-label={t('requests.openCount', { count: open.length })}>
            {open.length}
          </span>
        )}
      </h2>
      {failure === null ? null : <p className="panel__error">{failure}</p>}
      {open.length === 0 ? null : (
        <ul className="day-list day-list--compact">
          {open.map((request) =>
            line(
              request,
              <>
                {bookThis(request)}
                <button
                  className="link-button"
                  type="button"
                  disabled={busy === request.inquiry_id}
                  onClick={() => void markAnswered(request)}
                >
                  {t('requests.markAnswered')}
                </button>
              </>,
            ),
          )}
        </ul>
      )}
      {answered.length === 0 ? null : (
        <>
          <h3 className="muted">{t('requests.answered')}</h3>
          <ul className="day-list day-list--compact">
            {answered.map((request) => line(request, bookThis(request)))}
          </ul>
        </>
      )}
    </section>
  );
}

/** A request with no name is still a request; it is not a blank line. */
function nameOf(request: Inquiry, t: T): string {
  return request.name.trim() === '' ? t('requests.noName') : request.name;
}

/**
 * When they said suits them.
 *
 * `preferred` is the request form's free text ("weekday afternoons") and
 * `preferred_date` is the older `inquiry` block's `YYYY-MM-DD`. A note can
 * carry either and never both, so this is a choice rather than a join — and the
 * date is printed **as stored**, unparsed, because it was a wish typed into a
 * box and turning it into an instant would need a timezone decision about a
 * value nobody is going to schedule from.
 */
function preferenceOf(request: Inquiry): string {
  return request.preferred !== '' ? request.preferred : request.preferred_date;
}

/**
 * The second half of a "no" — her words, and the other time.
 *
 * Both live behind one disclosure rather than two buttons, because they are the
 * same moment: she is looking at a time she cannot take, and what happens next
 * is either a sentence or a different hour. Confirm stays a single tap outside
 * it, because saying yes should cost nothing.
 *
 * The proposed time is typed as a local date and time rather than picked off a
 * calendar, and that is a deliberate first version: the grid she would pick from
 * is the booking sheet's, which is a different screen with a different shape,
 * and wiring one into a row here is a wave of its own. What the server does with
 * it is the full check — the item, the lane, the house gap, the exclusion
 * constraint at confirm — so a time she types that does not work comes back as a
 * `409` with a sentence rather than as a silent overbooking.
 */
function RequestAnswer({
  request,
  busy,
  timezone,
  t,
  onCancel,
  onDecline,
  onPropose,
}: {
  request: StaffRequestType;
  busy: boolean;
  timezone: string;
  t: T;
  onCancel: () => void;
  onDecline: (message: string, trust: boolean | null) => void;
  onPropose: (startIso: string) => void;
}): ReactNode {
  const [message, setMessage] = useState('');
  const [when, setWhen] = useState('');

  return (
    <div className="request-answer">
      <label className="field">
        <span>{t('requests.declineMessage')}</span>
        <textarea
          rows={2}
          value={message}
          maxLength={500}
          onChange={(e) => setMessage(e.target.value)}
        />
      </label>
      <div className="row-actions">
        <button
          className="button"
          type="button"
          disabled={busy}
          onClick={() => onDecline(message, request.client.may_book_online ? false : null)}
        >
          {t('requests.decline')}
        </button>
      </div>

      <label className="field">
        <span>{t('requests.proposeStart')}</span>
        <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
      </label>
      <div className="row-actions">
        <button
          className="button"
          type="button"
          disabled={busy || when === ''}
          onClick={() => {
            const iso = localInputToIso(when, timezone);
            if (iso !== null) onPropose(iso);
          }}
        >
          {t('requests.propose')}
        </button>
        <button className="link-button" type="button" onClick={onCancel}>
          {t('requests.cancel')}
        </button>
      </div>
    </div>
  );
}

/**
 * `2026-09-24T15:00` in the tenant's zone → an instant.
 *
 * The two-pass trick the booking sheet uses: format a guess in the target zone,
 * measure how far off it landed, and correct. `Intl` is the only timezone
 * database in a browser and it converts one way; this is the inverse, and it is
 * correct across a DST boundary because the correction is measured *at* the
 * guessed instant rather than assumed from a fixed offset.
 */
function localInputToIso(value: string, timezone: string): string | null {
  const guess = Date.parse(`${value}:00Z`);
  if (Number.isNaN(guess)) return null;
  const offset = zoneOffsetMs(timezone, guess);
  const corrected = guess - offset;
  // One more pass, for the hour either side of a DST change.
  return new Date(corrected - (zoneOffsetMs(timezone, corrected) - offset)).toISOString();
}

function zoneOffsetMs(timezone: string, utcMs: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(utcMs));
  const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? '0');
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return asUtc - Math.floor(utcMs / 1000) * 1000;
}

/** One id per attempt — both `client_intent_id` and `Idempotency-Key`. */
function intentId(): string {
  const random = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (typeof random?.randomUUID === 'function') return random.randomUUID();
  return `i-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
